/** Pure presentation helpers for fiscal-year comparisons on the dashboard. */

export type FinancialTrend = 'up' | 'down' | 'flat' | 'unavailable';

export interface FinancialComparison {
  /** Percentage change, rounded once to one decimal place. */
  percent: number | null;
  direction: FinancialTrend;
  /** The previous value itself, for callers that need the number. */
  priorValue: number | null;
  /** The previous value already formatted, so callers do not duplicate formatting. */
  priorDisplay: string | null;
}

const finiteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Always format once with the app's dashboard currency and two decimals. */
export function formatFinancialCurrency(value: number): string {
  return `HKD ${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function roundToTenth(value: number): number {
  const rounded = Math.round(value * 10) / 10;
  return rounded === 0 ? 0 : rounded;
}

/**
 * Compare two fiscal-year amounts. A zero or unavailable previous amount has no
 * meaningful percentage, so the caller receives `unavailable` instead of a
 * misleading infinite or zero-percent comparison.
 */
export function compareFinancialValues(
  current: unknown,
  previous: unknown,
): FinancialComparison {
  const currentValue = finiteNumber(current);
  const priorValue = finiteNumber(previous);
  const priorDisplay = priorValue === null ? null : formatFinancialCurrency(priorValue);

  if (currentValue === null || priorValue === null || priorValue === 0) {
    return { percent: null, direction: 'unavailable', priorValue, priorDisplay };
  }

  const percent = roundToTenth(
    ((currentValue - priorValue) / Math.abs(priorValue)) * 100,
  );
  const direction: FinancialTrend = percent > 0 ? 'up' : percent < 0 ? 'down' : 'flat';

  return { percent, direction, priorValue, priorDisplay };
}

/**
 * Render the agreed dashboard pattern: `↑ 12.5% (HKD 980,000.00)`.
 * Zero and unavailable prior values intentionally render without an arrow.
 */
export function formatFinancialComparison(
  current: unknown,
  previous: unknown,
): string {
  const comparison = compareFinancialValues(current, previous);
  const percentDisplay = `${Math.abs(comparison.percent ?? 0).toFixed(1)}%`;

  if (comparison.direction === 'up') return `↑ ${percentDisplay} (${comparison.priorDisplay})`;
  if (comparison.direction === 'down') return `↓ ${percentDisplay} (${comparison.priorDisplay})`;
  return percentDisplay;
}

/**
 * Format a statement figure as a percentage of revenue. Revenue is rounded in
 * the same single operation as the comparison helper so displayed percentages
 * reconcile to one decimal place.
 */
export function formatPercentageOfRevenue(
  value: unknown,
  revenue: unknown,
): string | null {
  const amount = finiteNumber(value);
  const revenueAmount = finiteNumber(revenue);

  if (amount === null || revenueAmount === null || revenueAmount === 0) return null;
  return `${roundToTenth((amount / revenueAmount) * 100).toFixed(1)}%`;
}
